import { useMemo } from 'react';
import { convertToLocalTimezone, formatWithTimezone, getRelativeTime, formatChartTimestamp } from '../utils/timezone';

/**
 * Custom React hook for timezone-aware timestamp formatting
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York', 'Europe/Prague')
 * @returns {object} Formatting functions bound to the specified timezone
 */
export function useTimezone(timezone = 'UTC') {
    return useMemo(() => ({
        /**
         * Convert UTC timestamp to local timezone
         * @param {string|Date} timestamp - UTC timestamp
         * @param {object} options - Formatting options
         * @returns {string} Formatted date string
         */
        toLocal: (timestamp, options) => convertToLocalTimezone(timestamp, timezone, options),

        /**
         * Format timestamp with timezone information
         * @param {string|Date} timestamp - Timestamp
         * @param {boolean} showTz - Show timezone info
         * @returns {string} Formatted date with timezone
         */
        format: (timestamp, showTz = true) => formatWithTimezone(timestamp, timezone, showTz),

        /**
         * Get relative time (works regardless of timezone)
         * @param {string|Date} timestamp - Timestamp
         * @returns {string} Relative time string like "5 minutes ago"
         */
        relative: (timestamp) => getRelativeTime(timestamp),

        /**
         * Format timestamp for charts (short format)
         * @param {string|Date} timestamp - Timestamp
         * @returns {string} Short formatted date
         */
        forChart: (timestamp) => formatChartTimestamp(timestamp, timezone),

        /**
         * Get the timezone being used
         * @returns {string} IANA timezone string
         */
        getTimezone: () => timezone || 'UTC'
    }), [timezone]);
}

/**
 * Hook to get device's location timezone
 * @param {object} device - Device object with location_timezone or timezone property
 * @returns {object} Timezone formatting functions
 */
export function useDeviceTimezone(device) {
    const timezone = device?.location_timezone || device?.timezone || 'UTC';
    return useTimezone(timezone);
}
